import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MdAdd, MdSave } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { Modal } from '../../components/shared/Modal';
import { Pagination } from '../../components/shared/Pagination';
import { useToast } from '../../components/shared/Toast';
import { cn } from '../../lib/utils';
import {
  PageHeader,
  TableBodySkeleton,
  erpPageBtnPrimaryClass,
} from '@euroasiann/shared/ui';

const PAGE_SIZE = 10; // party agreements and versions per page

const fieldClass =
  'h-10 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]';

const labelClass = 'mb-1.5 block text-sm font-medium text-[hsl(var(--foreground))]';

const tableWrapClass =
  'overflow-x-auto rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm';

const thClass =
  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]';

const tdClass = 'px-4 py-3 text-sm text-[hsl(var(--foreground))]';

const trClass = 'border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted))]/40';

interface AgreementTypeRow {
  _id: string;
  code: string;
  name: string;
  active: boolean;
  requiresSignature: boolean;
  audiences: string[];
}

interface VersionRow {
  _id: string;
  versionNumber: string;
  title: string;
  status: string;
  contentHash: string;
  publishedAt?: string;
}

interface PartyRow {
  _id: string;
  partyType: string;
  partyEmail?: string;
  status: string;
  agreementVersionNumber: string;
  signedAt?: string;
}

const formFieldClass =
  'w-full h-10 px-3 text-sm rounded-md border bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/20 focus:border-[hsl(var(--primary))] transition-colors';

const formLabelClass = 'mb-1.5 block text-xs font-medium text-[hsl(var(--foreground))]';

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-[hsl(var(--border))]">
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-3 py-2">
        <h3 className="text-xs font-semibold tracking-wide text-[hsl(var(--foreground))]">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-[11px] leading-relaxed text-[hsl(var(--muted-foreground))]">{description}</p>
        ) : null}
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

function suggestNextVersion(versions: VersionRow[]): string {
  let maxMajor = 0;
  let maxMinor = 0;
  let found = false;
  for (const version of versions) {
    const match = String(version.versionNumber || '').trim().match(/^(\d+)(?:\.(\d+))?/);
    if (!match) continue;
    found = true;
    const major = Number(match[1]);
    const minor = Number(match[2] || 0);
    if (major > maxMajor || (major === maxMajor && minor > maxMinor)) {
      maxMajor = major;
      maxMinor = minor;
    }
  }
  if (!found) return '1.0';
  return `${maxMajor}.${maxMinor + 1}`;
}

function DraftVersionForm({
  type,
  versions,
  onSuccess,
  onCancel,
}: {
  type: AgreementTypeRow;
  versions: VersionRow[];
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [versionNumber, setVersionNumber] = useState(() => suggestNextVersion(versions));
  const [title, setTitle] = useState(type.name);
  const [contentFormat, setContentFormat] = useState<'text' | 'markdown' | 'html'>('text');
  const [contentSource, setContentSource] = useState('');
  const [importantTerms, setImportantTerms] = useState('');
  const [requiresSignature, setRequiresSignature] = useState(Boolean(type.requiresSignature));
  const [requiresAcceptance, setRequiresAcceptance] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!versionNumber.trim()) nextErrors.versionNumber = 'Version number is required';
    else if (!/^\d+(\.\d+)*$/.test(versionNumber.trim())) nextErrors.versionNumber = 'Use a number such as 1.0 or 3.3';
    if (!title.trim()) nextErrors.title = 'Title is required';
    if (!contentSource.trim()) nextErrors.contentSource = 'Agreement content is required';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const res = await authenticatedFetch(`/api/v1/agreements/types/${type._id}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          versionNumber: versionNumber.trim(),
          title: title.trim(),
          contentSource: contentSource.trim(),
          contentFormat,
          importantTerms: importantTerms
            .split(/[\n,]/)
            .map((term) => term.trim())
            .filter(Boolean),
          requiresSignature,
          requiresAcceptance,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Create version failed');
      toast.success('Draft version created');
      onSuccess();
    } catch (err: any) {
      const message = err.message || 'Create version failed';
      if (/version/i.test(message) && /exist|duplicate|unique/i.test(message)) {
        setErrors({ versionNumber: message });
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-3">
      <FormSection title="Version identity" description="This draft is saved against the selected agreement type and stays unpublished until you publish it.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={formLabelClass}>Agreement type</label>
            <div className={cn(formFieldClass, 'flex items-center border-[hsl(var(--border))] bg-[hsl(var(--muted))] cursor-default')}>
              <span className="truncate">{type.name}</span>
              <span className="ml-2 shrink-0 font-mono text-[11px] text-[#0088cc]">{type.code}</span>
            </div>
          </div>
          <div>
            <label className={formLabelClass}>
              Version number <span className="text-red-500">*</span>
            </label>
            <input
              value={versionNumber}
              onChange={(e) => setVersionNumber(e.target.value)}
              className={cn(formFieldClass, errors.versionNumber ? 'border-red-400' : 'border-[hsl(var(--border))]')}
              placeholder="e.g. 3.3"
              autoComplete="off"
            />
            {errors.versionNumber ? (
              <p className="mt-1 text-xs text-red-600">{errors.versionNumber}</p>
            ) : (
              <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">Suggested from the latest version of this type.</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className={formLabelClass}>
              Display title <span className="text-red-500">*</span>
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={cn(formFieldClass, errors.title ? 'border-red-400' : 'border-[hsl(var(--border))]')}
              placeholder="Shown to the signing party"
            />
            {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
          </div>
        </div>
      </FormSection>

      <FormSection title="Agreement body" description="Paste the full terms. Published versions are immutable, so review before you publish.">
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={formLabelClass}>Content format</label>
              <select
                value={contentFormat}
                onChange={(e) => setContentFormat(e.target.value as 'text' | 'markdown' | 'html')}
                className={cn(formFieldClass, 'border-[hsl(var(--border))]')}
              >
                <option value="text">Plain text</option>
                <option value="markdown">Markdown</option>
                <option value="html">HTML</option>
              </select>
            </div>
            <div>
              <label className={formLabelClass}>Important terms (optional)</label>
              <input
                value={importantTerms}
                onChange={(e) => setImportantTerms(e.target.value)}
                className={cn(formFieldClass, 'border-[hsl(var(--border))]')}
                placeholder="Comma or line separated highlights"
              />
            </div>
          </div>
          <div>
            <label className={formLabelClass}>
              Terms content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={contentSource}
              onChange={(e) => setContentSource(e.target.value)}
              rows={9}
              className={cn(
                'w-full resize-y rounded-md border bg-[hsl(var(--background))] px-3 py-2 text-sm leading-relaxed text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/20 focus:border-[hsl(var(--primary))]',
                errors.contentSource ? 'border-red-400' : 'border-[hsl(var(--border))]'
              )}
              placeholder="Paste the complete agreement text the party will review and accept."
            />
            <div className="mt-1 flex items-center justify-between gap-3">
              {errors.contentSource ? (
                <p className="text-xs text-red-600">{errors.contentSource}</p>
              ) : (
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">This becomes the system-of-record text for this version.</p>
              )}
              <p className="shrink-0 text-[11px] text-[hsl(var(--muted-foreground))]">
                {contentSource.trim().length.toLocaleString()} characters
              </p>
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection title="Party requirements">
        <div className="space-y-2.5">
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={requiresAcceptance}
              onChange={(e) => setRequiresAcceptance(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
            />
            <span>
              <span className="block text-sm text-[hsl(var(--foreground))]">Require terms acceptance</span>
              <span className="block text-[11px] text-[hsl(var(--muted-foreground))]">Party must confirm they have read and are authorized to accept.</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={requiresSignature}
              onChange={(e) => setRequiresSignature(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
            />
            <span>
              <span className="block text-sm text-[hsl(var(--foreground))]">Require electronic signature</span>
              <span className="block text-[11px] text-[hsl(var(--muted-foreground))]">Default is taken from this agreement type and can be overridden for this draft.</span>
            </span>
          </label>
        </div>
      </FormSection>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-9 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 text-sm font-medium text-[hsl(var(--foreground))] transition hover:bg-[hsl(var(--muted))] disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-[hsl(var(--primary))] px-4 text-sm font-semibold text-[hsl(var(--primary-foreground))] hover:opacity-90 disabled:opacity-60"
        >
          <MdSave className="h-4 w-4" />
          {saving ? 'Saving…' : 'Save draft'}
        </button>
      </div>
    </form>
  );
}

function statusBadge(status: string) {
  const value = (status || '').toUpperCase();
  const active = value === 'PUBLISHED' || value === 'SIGNED' || value === 'ACTIVE' || value === 'ACCEPTED';
  const pending = value === 'DRAFT' || value === 'REVIEW' || value === 'PENDING' || value === 'IN_PROGRESS';
  const className = active
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
    : pending
      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
      : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]';
  return (
    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${className}`}>
      {status || '—'}
    </span>
  );
}

export default function AgreementManagementPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [versionPage, setVersionPage] = useState(1);
  const [partyPage, setPartyPage] = useState(1);
  const [partyTypeFilter, setPartyTypeFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);

  const typesQuery = useQuery({
    queryKey: ['agreement-types'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/v1/agreements/types');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load types');
      return (json.data || []) as AgreementTypeRow[];
    },
  });

  const types = typesQuery.data || [];

  useEffect(() => {
    if (!selectedTypeId && types[0]?._id) {
      setSelectedTypeId(types[0]._id);
    }
  }, [types, selectedTypeId]);

  const versionsQuery = useQuery({
    queryKey: ['agreement-versions', selectedTypeId],
    enabled: Boolean(selectedTypeId),
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/v1/agreements/types/${selectedTypeId}/versions`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load versions');
      return (json.data || []) as VersionRow[];
    },
  });

  const versions = versionsQuery.data || [];
  const versionTotalPages = Math.max(1, Math.ceil(versions.length / PAGE_SIZE));
  const pagedVersions = useMemo(() => {
    const start = (versionPage - 1) * PAGE_SIZE;
    return versions.slice(start, start + PAGE_SIZE);
  }, [versions, versionPage]);

  useEffect(() => {
    setVersionPage(1);
  }, [selectedTypeId]);

  useEffect(() => {
    if (versionPage > versionTotalPages) setVersionPage(versionTotalPages);
  }, [versionPage, versionTotalPages]);

  const partiesQuery = useQuery({
    queryKey: ['party-agreements', partyPage, partyTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(partyPage));
      params.set('limit', String(PAGE_SIZE));
      if (partyTypeFilter) params.set('partyType', partyTypeFilter);
      const res = await authenticatedFetch(`/api/v1/agreements/party?${params}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load party agreements');
      return {
        data: ((json.data || []) as PartyRow[]).slice(0, PAGE_SIZE),
        meta: json.meta || { total: Array.isArray(json.data) ? json.data.length : 0, page: partyPage, limit: PAGE_SIZE },
      };
    },
  });

  const parties = partiesQuery.data?.data || [];
  const partyMeta = partiesQuery.data?.meta || { total: 0, page: partyPage, limit: PAGE_SIZE };
  const partyTotalPages = Math.max(1, Math.ceil((partyMeta.total || 0) / PAGE_SIZE));

  useEffect(() => {
    if (partyPage > partyTotalPages) setPartyPage(partyTotalPages);
  }, [partyPage, partyTotalPages]);

  const selectedType = types.find((t) => t._id === selectedTypeId);
  const loadError =
    typesQuery.error instanceof Error
      ? typesQuery.error.message
      : versionsQuery.error instanceof Error
        ? versionsQuery.error.message
        : partiesQuery.error instanceof Error
          ? partiesQuery.error.message
          : null;

  const publish = async (versionId: string) => {
    setBusy(true);
    try {
      const res = await authenticatedFetch(`/api/v1/agreements/versions/${versionId}/publish`, {
        method: 'POST',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Publish failed');
      toast.success('Version published');
      await queryClient.invalidateQueries({ queryKey: ['agreement-versions', selectedTypeId] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const closeDraft = () => setDraftOpen(false);

  return (
    <div className="w-full space-y-6">
      <PageHeader
        title="Agreement Management"
        subtitle="Types, versions, and vendor, customer, and port-agent agreements."
        subtitleVariant="description"
        subtitleAccent={false}
        breadcrumb={[
          { label: 'Admin' },
          { label: 'Agreement Management', current: true },
        ]}
      />

      {loadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
          {loadError}
        </div>
      )}

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <div>
            <h2 className="text-base font-semibold text-[hsl(var(--foreground))]">Agreement types</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Select a type to view its versions</p>
          </div>
          <div className={tableWrapClass}>
            <table className="w-full min-w-[420px] border-collapse">
              <thead>
                <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
                  {['Type', 'Audience', 'Signature'].map((h) => (
                    <th key={h} className={thClass}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {typesQuery.isLoading ? (
                  <TableBodySkeleton rows={4} cols={3} />
                ) : types.length === 0 ? (
                  <tr>
                    <td colSpan={3} className={`${tdClass} py-12 text-center text-[hsl(var(--muted-foreground))]`}>
                      No agreement types
                    </td>
                  </tr>
                ) : (
                  types.map((t) => (
                    <tr
                      key={t._id}
                      className={`${trClass} cursor-pointer ${selectedTypeId === t._id ? 'bg-[#0088cc]/8' : ''}`}
                      onClick={() => setSelectedTypeId(t._id)}
                    >
                      <td className={tdClass}>
                        <div className="font-medium">{t.name}</div>
                        <div className="mt-0.5 font-mono text-xs text-[#0088cc]">{t.code}</div>
                      </td>
                      <td className={`${tdClass} text-[hsl(var(--muted-foreground))]`}>
                        {t.audiences?.join(', ') || '—'}
                      </td>
                      <td className={tdClass}>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                          {t.requiresSignature ? 'Required' : 'Acceptance only'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-[hsl(var(--foreground))]">Versions</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {selectedType ? selectedType.name : 'Select a type'}
              </p>
            </div>
            <button
              type="button"
              disabled={busy || !selectedTypeId}
              onClick={() => setDraftOpen(true)}
              className={erpPageBtnPrimaryClass}
            >
              <MdAdd className="h-4 w-4" />
              New draft
            </button>
          </div>
          <div className={tableWrapClass}>
            <table className="w-full min-w-[480px] border-collapse">
              <thead>
                <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
                  {['Version', 'Status', 'Hash', 'Actions'].map((h) => (
                    <th key={h} className={thClass}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {versionsQuery.isLoading ? (
                  <TableBodySkeleton rows={4} cols={4} />
                ) : pagedVersions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={`${tdClass} py-12 text-center text-[hsl(var(--muted-foreground))]`}>
                      No versions for this type
                    </td>
                  </tr>
                ) : (
                  pagedVersions.map((v) => (
                    <tr key={v._id} className={trClass}>
                      <td className={`${tdClass} font-medium`}>{v.versionNumber}</td>
                      <td className={tdClass}>{statusBadge(v.status)}</td>
                      <td className={`${tdClass} font-mono text-xs text-[hsl(var(--muted-foreground))]`}>
                        {v.contentHash ? `${v.contentHash.slice(0, 12)}…` : '—'}
                      </td>
                      <td className={tdClass}>
                        {(v.status === 'DRAFT' || v.status === 'REVIEW') && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => publish(v._id)}
                            className="text-sm font-semibold text-[#0088cc] hover:underline disabled:opacity-50"
                          >
                            Publish
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {versions.length > 0 && (
              <Pagination
                currentPage={versionPage}
                totalPages={versionTotalPages}
                onPageChange={setVersionPage}
                itemsPerPage={PAGE_SIZE}
                totalItems={versions.length}
              />
            )}
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Published versions are immutable. Create a new version to change terms.
          </p>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-[hsl(var(--foreground))]">Party agreements</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {partyMeta.total ? `${partyMeta.total.toLocaleString()} records · 10 per page` : 'Signed and in-progress party records'}
            </p>
          </div>
          <div className="w-full sm:w-52">
            <label className={labelClass}>Party type</label>
            <select
              value={partyTypeFilter}
              onChange={(e) => {
                setPartyTypeFilter(e.target.value);
                setPartyPage(1);
              }}
              className={fieldClass}
            >
              <option value="">All parties</option>
              <option value="vendor">Vendor</option>
              <option value="customer">Customer</option>
              <option value="port_agent">Port agent</option>
            </select>
          </div>
        </div>

        <div className={tableWrapClass}>
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
                {['Party', 'Email', 'Version', 'Status', 'Signed'].map((h) => (
                  <th key={h} className={thClass}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {partiesQuery.isLoading ? (
                <TableBodySkeleton rows={6} cols={5} />
              ) : parties.length === 0 ? (
                <tr>
                  <td colSpan={5} className={`${tdClass} py-12 text-center text-[hsl(var(--muted-foreground))]`}>
                    No party agreements yet
                  </td>
                </tr>
              ) : (
                parties.map((p) => (
                  <tr key={p._id} className={trClass}>
                    <td className={`${tdClass} capitalize`}>{p.partyType?.replace('_', ' ')}</td>
                    <td className={tdClass}>{p.partyEmail || '—'}</td>
                    <td className={tdClass}>{p.agreementVersionNumber}</td>
                    <td className={tdClass}>{statusBadge(p.status)}</td>
                    <td className={`${tdClass} text-[hsl(var(--muted-foreground))]`}>
                      {p.signedAt ? new Date(p.signedAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {(partyMeta.total || 0) > 0 && (
            <Pagination
              currentPage={partyPage}
              totalPages={partyTotalPages}
              onPageChange={setPartyPage}
              itemsPerPage={PAGE_SIZE}
              totalItems={partyMeta.total}
            />
          )}
        </div>
      </section>

      <Modal
        isOpen={draftOpen}
        onClose={closeDraft}
        title="Create draft version"
        description="Save an unpublished revision of this agreement. Published versions cannot be edited — create a new draft to change terms."
        size="medium"
        compact
      >
        {selectedType ? (
          <DraftVersionForm
            key={`${selectedType._id}-${draftOpen}`}
            type={selectedType}
            versions={versions}
            onCancel={closeDraft}
            onSuccess={async () => {
              closeDraft();
              setVersionPage(1);
              await queryClient.invalidateQueries({ queryKey: ['agreement-versions', selectedTypeId] });
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
}
